import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type MatchPlayerSlot =
  | 'team1_player1'
  | 'team1_player2'
  | 'team2_player1'
  | 'team2_player2';

interface ReplaceArgs {
  matchId: string;
  groupId?: string | null;
  slot: MatchPlayerSlot;
  newUserId: string;
  oldUserId?: string | null;
  matchDate?: string;
  clubName?: string | null;
}

export function useReplaceScheduledMatchPlayer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ matchId, slot, newUserId, oldUserId, matchDate, clubName }: ReplaceArgs) => {
      const { error } = await supabase
        .from('published_matches')
        .update({ [slot]: newUserId })
        .eq('id', matchId);

      if (error) throw error;

      const content = {
        match_id: matchId,
        match_date: matchDate,
        club_name: clubName,
      };

      const notifications: any[] = [
        {
          user_id: newUserId,
          type: 'match_player_added',
          title: 'Added to a scheduled match',
          message: 'You have been added to a scheduled match.',
          content,
          is_read: false,
        },
      ];

      if (oldUserId && oldUserId !== newUserId) {
        notifications.push({
          user_id: oldUserId,
          type: 'match_player_removed',
          title: 'Removed from a scheduled match',
          message: 'You have been removed from a scheduled match.',
          content,
          is_read: false,
        });
      }

      await supabase.from('notifications').insert(notifications);
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-matches'] });
      queryClient.invalidateQueries({ queryKey: ['group-scheduled-matches', vars.groupId] });
      queryClient.invalidateQueries({ queryKey: ['open-matches'] });
      toast.success('Player replaced');
    },
    onError: (err: any) => {
      console.error('Replace player error', err);
      toast.error(err?.message || 'Failed to replace player');
    },
  });
}
